import React, { useState, useMemo, useEffect } from 'react';
import ReportLayout, {
  DateRangePicker,
  StatusBadge,
  ProgressBar
} from './components/ReportLayout';
import {
  Percent,
  DollarSign,
  Receipt,
  TrendingUp,
  Tag,
  BarChart3,
  Gift,
  Target,
  Building2,
  ChevronDown
} from 'lucide-react';

import useDiscountAnalysis from '@/hooks/useDiscountAnalysis';
import ReportsService from '@/services/reportsService';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

const PERIOD_MAP = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this_week',
  thisMonth: 'this_month',
  lastMonth: 'last_month',
};

const DISCOUNT_CODES = [
  { id: 0, code: 'All Discounts' },
  { id: 1, code: 'WEEKEND20' },
  { id: 2, code: 'FLAT100' },
  { id: 3, code: 'BOGO' },
  { id: 4, code: 'DINEIN10' },
  { id: 5, code: 'NEWUSER' },
];

export default function DiscountAnalysisReport() {
  const { hasPermission } = useAuth();

  if (!hasPermission(P.REPORTS.FINANCIAL.SCREEN)) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 font-semibold">
          You do not have permission to access Discount Analysis Report
        </p>
      </div>
    );
  }
  const exportToCsv = () => {
    if (!data?.length) return;

    const headers = columns.map(c => `"${c.label}"`).join(',');
    const rows = data.map(r =>
      columns.map(c => `"${r[c.key] ?? ''}"`).join(',')
    );

    const blob = new Blob([headers, '\n', rows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'discount-analysis-reports.csv';
    link.click();
  };

  const today = new Date().toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedPreset, setSelectedPreset] = useState('today');
  const [selectedDiscount, setSelectedDiscount] = useState('');
  const [discountCodes, setDiscountCodes] = useState([
    { id: '', code: 'All Discounts' }
  ]);
  const [sortColumn, setSortColumn] = useState('totalDiscountGiven');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');

  const [isCustomDate, setIsCustomDate] = useState(false);
  const [isManualFilter, setIsManualFilter] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [lastFetchParams, setLastFetchParams] = useState({ period: PERIOD_MAP['today'] });
  const {
    data,
    summary,
    branchSummary,
    total,
    loading,
    error,
    filters,
    setFilters,
    fetchDiscountAnalysis
  } = useDiscountAnalysis({
    offset: 0,
    limit: 25
  });

  useEffect(() => {
    const params = { period: PERIOD_MAP['today'] };
    setLastFetchParams(params);
    fetchDiscountAnalysis(params);

    const loadDiscountCodes = async () => {
      try {
        const res = await ReportsService.getDiscountCodes();

        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : [];

        setDiscountCodes([
          { id: '', code: 'All Discounts' },
          ...list
        ]);
      } catch (err) {
        console.error('Failed to fetch discount codes', err);
      }
    };

    loadDiscountCodes();
  }, []);

  // Cache branch options from the initial (unfiltered) load so dropdown persists
  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({
        id: b.branchId,
        name: b.branchName || b.branchId,
      })));
    }
  }, [branchSummary, selectedBranchId]);

  const branchOptions = cachedBranchOptions;

  const summaryCards = [
    {
      title: 'Gross Sales',
      value: `₹${(summary?.grossSales ?? 0).toLocaleString()}`,
      subtitle: 'Before discounts',
      icon: DollarSign
    },
    {
      title: 'Total Discount',
      value: `₹${(summary?.totalDiscountGiven ?? summary?.totalDiscount ?? 0).toLocaleString()}`,
      subtitle: 'Discount given',
      icon: Percent
    },
    {
      title: 'Net Sales',
      value: `₹${(summary?.netSales ?? 0).toLocaleString()}`,
      subtitle: 'After discounts',
      icon: Receipt
    },
    {
      title: 'Discount Rate',
      value: `${summary?.overallDiscountPercentage ?? 0}%`,
      subtitle: 'Overall discount %',
      icon: BarChart3
    },
    {
      title: 'Bills with Discount',
      value: `${summary?.billsWithDiscount ?? 0}`,
      subtitle: 'Discount applied',
      icon: Tag
    },
    {
      title: 'Avg per Bill',
      value: `₹${summary?.avgDiscountPerBill?.toFixed?.(0) ?? 0}`,
      subtitle: 'Avg discount/bill',
      icon: Target
    }
  ];

  const columns = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-sm font-semibold text-purple-700 bg-purple-50 px-2 py-1 rounded">{value}</span>
      )
    },
    {
      key: 'name',
      label: 'Discount Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge
              status={row.discountType === 'percentage' ? 'Percentage' : row.discountType === 'fixed' ? 'Fixed' : 'BOGO'}
              type={row.discountType === 'percentage' ? 'info' : row.discountType === 'fixed' ? 'success' : 'warning'}
            />
            {row.discountType !== 'bogo' && (
              <span className="text-xs text-gray-500">
                {row.discountType === 'percentage' ? `${row.discountValue}%` : `₹${row.discountValue}`}
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'timesUsed',
      label: 'Times Used',
      sortable: true,
      align: 'right'
    },
    {
      key: 'totalDiscountGiven',
      label: 'Total Discount',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className="font-semibold text-red-600">-₹{value?.toLocaleString?.()}</span>
      )
    },
    {
      key: 'avgDiscountPerUse',
      label: 'Avg per Use',
      sortable: true,
      align: 'right'
    },
    {
      key: 'billsGenerated',
      label: 'Revenue',
      sortable: true,
      align: 'right'
    },
    {
      key: 'effectiveDiscountRate',
        label: 'Effective Rate',
        sortable: true,
        align: 'right',
        width: '120px',
        render: (value) => (
          <span className="text-sm font-semibold text-gray-900">
            {value?.toFixed(1)}%
          </span>
        )

    }
  ];

  const handleRefresh = () => {
    fetchDiscountAnalysis({
      ...lastFetchParams,
    });
  };

const handlePresetSelect = (preset) => {
  setSelectedPreset(preset);
  setIsCustomDate(false);
  setIsManualFilter(false);

  const params = {
    period: PERIOD_MAP[preset],
    discountId: selectedDiscount || undefined,
    branchId: selectedBranchId || undefined,
    offset: 0,
    limit: pageSize,
  };
  setLastFetchParams(params);
  fetchDiscountAnalysis(params);
};


  const filterUI = (
    <div className="space-y-4">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        selectedPreset={selectedPreset}
        onPresetSelect={handlePresetSelect}
        onStartDateChange={(d) => {
          setStartDate(d);
          setSelectedPreset("");
          setIsCustomDate(true);
          setIsManualFilter(true);
        }}
        onEndDateChange={(d) => {
          setEndDate(d);
          setSelectedPreset("");
          setIsCustomDate(true);
          setIsManualFilter(true);
        }}
      />

      <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-gray-200">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">
            Discount Code
          </label>
          <select
            value={selectedDiscount}
            onChange={(e) => {
              setSelectedDiscount(e.target.value);
              setIsManualFilter(true);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
          >
            {discountCodes.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-transparent">Apply</label>
          <button
            disabled={!isManualFilter}
            onClick={() => {
              const params = isCustomDate
                ? {
                    startDate,
                    endDate,
                    discountId: selectedDiscount || undefined,
                    branchId: selectedBranchId || undefined,
                    period: null,
                    offset: 0,
                    limit: pageSize,
                  }
                : {
                    period: PERIOD_MAP[selectedPreset],
                    discountId: selectedDiscount || undefined,
                    branchId: selectedBranchId || undefined,
                    startDate: null,
                    endDate: null,
                    offset: 0,
                    limit: pageSize,
                  };
              setLastFetchParams(params);
              fetchDiscountAnalysis(params);

              setIsManualFilter(false);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              isManualFilter
                ? "bg-teal-600 text-white hover:bg-teal-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Apply Filters
          </button>
        </div>

        {branchOptions.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Branch</label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={selectedBranchId ?? ''}
                onChange={(e) => {
                  const branchId = e.target.value || null;
                  setSelectedBranchId(branchId);
                  const params = { ...lastFetchParams, branchId: branchId || undefined, offset: 0 };
                  setLastFetchParams(params);
                  fetchDiscountAnalysis(params);
                }}
                className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
              >
                <option value="">All Branches</option>
                {branchOptions.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const pagination = {
    currentPage,
    totalPages: Math.ceil(total / pageSize),
    totalItems: total,
    pageSize,
    onPageChange: (page) => {
      setCurrentPage(page);
      const params = { ...lastFetchParams, offset: (page - 1) * pageSize, limit: pageSize };
      setLastFetchParams(params);
      fetchDiscountAnalysis(params);
    },
    onPageSizeChange: (size) => {
      setPageSize(size);
      setCurrentPage(1);
      const params = { ...lastFetchParams, limit: size, offset: 0 };
      setLastFetchParams(params);
      fetchDiscountAnalysis(params);
    }
  };
  
 //SORTING
const sortedData = useMemo(() => {
  if (!data || !sortColumn) return data;

  const sorted = [...data];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === 'asc'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
  });

  return sorted;
}, [data, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in code and name
    if (String(row.code || '').toLowerCase().includes(query)) return true;
    if (String(row.name || '').toLowerCase().includes(query)) return true;
    // Search in numeric fields
    const numericFields = ['timesUsed', 'totalDiscountGiven', 'avgDiscountPerUse', 'billsGenerated', 'effectiveDiscountRate'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Discount Analysis Report"
      description="Track discount code performance and measure promotion ROI"
      icon={Percent}
      exportPermission={P.REPORTS.FINANCIAL.DISCOUNT_EXPORT}
      color="emerald"
      category="Financial"
      categoryId="financial"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filterUI}
      loading={loading}
      error={error}
      pagination={pagination}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by code, name..."

      onSort={(column) => {
      let nextDirection;

      if (sortColumn === column) {
        nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        nextDirection = 'desc';
      }

      setSortColumn(column);
      setSortDirection(nextDirection);
      }}

      onRefresh={handleRefresh}
      onExport={(format) => {
        if (format === 'csv') exportToCsv();
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp size={16} className="text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Best ROI Discounts</h3>
              <p className="text-xs text-gray-500">
                Highest revenue per discount rupee
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {[...data]
              ?.filter(d => d.totalDiscountGiven > 0)
              ?.sort(
                (a, b) =>
                  (b.billsGenerated / b.totalDiscountGiven) -
                  (a.billsGenerated / a.totalDiscountGiven)
              )
              ?.slice(0, 3)
              ?.map((item, index) => (
                <div
                  key={item.discountId}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.code}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.timesUsed} uses
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-sm font-semibold text-green-600">
                      ₹{(item.billsGenerated / item.totalDiscountGiven).toFixed(1)}
                    </p>
                    <p className="text-xs text-gray-400">per ₹1 discount</p>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Gift size={16} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Most Popular</h3>
              <p className="text-xs text-gray-500">Highest usage count</p>
            </div>
          </div>

          <div className="space-y-3">
            {[...data]
              ?.sort((a, b) => b.timesUsed - a.timesUsed)
              ?.slice(0, 3)
              ?.map((item, index) => (
                <div
                  key={item.discountId}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0
                          ? "bg-purple-100 text-purple-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {index + 1}
                    </span>

                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.code}
                      </p>
                      <p className="text-xs text-gray-500">{item.name}</p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-sm font-semibold text-purple-600">
                      {item.timesUsed}
                    </p>
                    <p className="text-xs text-gray-400">times used</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </ReportLayout>
  );
}
